import React, { DragEvent, HTMLAttributes, useState } from 'react';
import { Typography, Button, CircularProgress, Theme, styled, lighten, alpha, Fade } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { CheckCircle } from '@mui/icons-material';
import { api } from 'services/api';
import { useMessaging } from 'providers/messaging';
import { Image } from 'types/image';
import { DefaultImage } from 'types/defaultImage';

const useStyles = makeStyles<Theme>(theme => ({
  container: {
    width: '100%',
  },
  image: {
    width: '100%',
    borderRadius: 4,
    maxWidth: 500,
    maxHeight: 500,
    objectFit: 'cover',
  },
  inDraggableZone: {
    border: `3px dashed ${theme.palette.primary.main}`,
    opacity: 0.5,
  },
  imageWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    display: 'flex',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
    '& > svg': {
      position: 'absolute',
      bottom: 0,
      right: 0,
    },
  },
  label: {
    display: 'flex',
    cursor: 'pointer',
    minWidth: 110,
  },
  icon: {
    backgroundColor: '#fff',
    borderRadius: '50%',
  },
  loading: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    display: 'flex',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    backgroundColor: 'rgba(250, 250, 250, 1)',
  },
  linkNewImage: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    display: 'inline-flex',
    marginBottom: 15,
    textAlign: 'center',
    padding: 10,
  },
  helperText: {
    position: 'absolute',
    bottom: 10,
  },
}));

export const ImageContainer = styled('div', { shouldForwardProp: prop => prop !== 'error' })<{ error: any }>(
  ({ theme, error }) => ({
    display: 'flex',
    width: '100%',
    height: 240,
    border: `2px dashed ${error ? theme.palette.error.main : lighten(theme.palette.primary.main, 0.7)}`,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      marginBottom: 15,
    },
  }),
);

interface ImageUploadProps extends HTMLAttributes<HTMLDivElement> {
  image: DefaultImage | null;
  validationError?: string;
  label: string;
  handleRemoveImage(): void;
  handleSetImage(image: Image | null): void;
  containerClassname?: string;
  disabled?: boolean;
  hideImageSelectorBackdrop?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  image,
  handleRemoveImage,
  handleSetImage,
  validationError,
  label,
  className,
  containerClassname,
  disabled = false,
  hideImageSelectorBackdrop = false,
  ...rest
}) => {
  const classes = useStyles({ error: !!validationError });
  const [selected, setSelected] = useState(false);
  const [dragIn, setDragIn] = useState(false);
  const [saving, setSaving] = useState(false);
  const messaging = useMessaging();

  function handleUploadFiles(files: FileList | null) {
    if (!files) return;

    const form = new FormData();
    Array.from(files).forEach((file, index) => {
      form.append(`files[${index}]`, file);
    });

    form.append('is_public', '1');

    setSaving(true);

    api
      .post('/images', form)
      .then(response => {
        handleSetImage(response.data[0]);
      })
      .catch(() => {
        messaging.handleOpen('Não foi possível carregar a imagem');
      })
      .finally(() => {
        setSaving(false);
      });
  }

  function handleDropFile(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    handleUploadFiles(files);
    setDragIn(false);
  }

  function handleDragEnter(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragIn(true);
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragIn(false);
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragIn(true);
  }

  return (
    <div className={className ? `${classes.container} ${className}` : classes.container}>
      {!image ? (
        <ImageContainer
          error={validationError}
          {...rest}
          className={dragIn ? `${classes.inDraggableZone} ${containerClassname || ''}` : containerClassname}
          onDrop={e => handleDropFile(e)}
          onDragLeave={e => handleDragLeave(e)}
          onDragEnter={e => handleDragEnter(e)}
          onDragOver={e => handleDragOver(e)}
          draggable
        >
          {saving ? (
            <CircularProgress />
          ) : (
            <>
              <input
                type="file"
                style={{ display: 'none' }}
                id="input-file"
                onChange={event => handleUploadFiles(event.target.files)}
                accept="image/*"
              />
              <label htmlFor="input-file">
                <span className={classes.linkNewImage}>Escolher uma nova imagem</span>
              </label>

              <Typography className={classes.helperText} variant="caption" color="textSecondary" align="center">
                Você também pode arrastar uma imagem aqui
              </Typography>
            </>
          )}
        </ImageContainer>
      ) : (
        <ImageContainer
          error={validationError}
          className={containerClassname}
          onMouseEnter={() => setSelected(selected => !selected)}
          onMouseLeave={() => setSelected(selected => !selected)}
        >
          <Fade in={selected}>
            <div className={classes.imageWrapper}>
              <CheckCircle className={classes.icon} color="primary" />
              <Button size="small" variant="contained" color="primary" onClick={handleRemoveImage}>
                Remover
              </Button>
            </div>
          </Fade>
          <img alt="Foto do produto" className={classes.image} src={image.image?.imageUrl} />
        </ImageContainer>
      )}
      {validationError ? (
        <Typography variant="caption" color="error">
          {validationError}
        </Typography>
      ) : (
        <Typography variant="caption" display="block">
          {label}
        </Typography>
      )}
    </div>
  );
};

export default ImageUpload;
